import { useState, useEffect, useRef, useCallback } from 'react';
import Dropdown from '../../Components/CustomDropdown/customdropdown';
import { debounce } from 'lodash';
import { useToast } from '../../Utils/toastContext';
import { useNavigate, useLocation } from 'react-router-dom';

export default function AddQuestionModal({
  close,
  questionData,
  getQuestions,
  existingQuestions,
  setQuestions,
}) {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [options, setOptions] = useState([{ id: 1, value: '' }]);
  const [question, setQuestion] = useState('');
  const [questionType, setQuestionType] = useState(1);
  const [questionTypeLabel, setQuestionTypeLabel] = useState('Text');
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [required, setRequired] = useState(false);
  const [preSelectedQuestionType, setPreSelectedQuestionType] = useState({
    value: 1,
    label: 'Text',
  });
  const [questionTypeData, setQuestionTypeData] = useState([]);
  const buildQuestionObject = () => ({
    id: questionData?.id || `q_${Date.now()}`,
    question,
    type: {
      value: questionType,
      label: questionTypeLabel,
    },
    required,
    options: options.filter((opt) => opt.value?.trim()),
    isFromApi: false,
  });
  // Simple option text change
  const handleOptionChange = (value, index) => {
    const newOptions = [...options];
    newOptions[index].value = value;
    setOptions(newOptions);
  };

  // Remove option
  const removeOption = (index) => {
    setOptions((prev) => prev.filter((_, i) => i !== index));
  };

  // Add new option
  const addOption = () => {
    setOptions((prev) => [
      ...prev,
      {
        id: prev.length ? prev[prev.length - 1].id + 1 : 1,
        value: '',
      },
    ]);
  };
  // Checkbox toggle for type 4
  const handleCheckboxChange = (index) => {
    const newOptions = [...options];
    newOptions[index].checked = !newOptions[index].checked;
    setOptions(newOptions);
  };

  // Percent change for type 4
  const handlePercentChange = (index, percent) => {
    const newOptions = [...options];
    newOptions[index].percent = percent;
    setOptions(newOptions);
  };
  const handleSubmit = () => {
    const newQuestion = buildQuestionObject();

    let updatedQuestions = [];

    if (questionData) {
      // ✏️ EDIT
      updatedQuestions = existingQuestions.map((q) =>
        q.id === questionData.id ? newQuestion : q,
      );
    } else {
      // ➕ ADD
      updatedQuestions = [...existingQuestions, newQuestion];
    }

    // ✅ Update parent state (MAIN THING)
    setQuestions(updatedQuestions);

    // (Optional) send to API
    // await updateTemplateAPI(updatedQuestions);

    close();
  };
  useEffect(() => {
    if (questionData) {
      setQuestion(questionData.question);
      setQuestionType(questionData.type.value);
      setQuestionTypeLabel(questionData.type.label);
      setRequired(questionData.required);
      setOptions(
        questionData.options?.length
          ? questionData.options
          : [{ id: 1, value: '' }],
      );
      setPreSelectedQuestionType(questionData.type);
    }
  }, [questionData]);

  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const formatName = (name) => {
    if (!name) return '';

    return name
      .replace(/_/g, ' ') // replace underscores with space
      .toLowerCase() // make all lowercase first
      .replace(/\b\w/g, (char) => char.toUpperCase()); // capitalize each word
  };
  const handleSelectQuestionType = (selectedOption) => {
    setQuestionType(selectedOption.value);
    setQuestionTypeLabel(selectedOption.label);
  };
  const getQuestionTypes = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/edd/question-types`);

      if (response.status == 200) {
        const AccountData = [];
        response.data.forEach((profile) => {
          AccountData.push({
            value: profile.id,
            label: formatName(profile.value),
          });
        });
        setQuestionTypeData(AccountData);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };

  useEffect(() => {
    getQuestionTypes();
  }, []);
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 font-mediuum">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      <div className="bg-white w-[90%] md:w-[500px] rounded-xl p-6 shadow-lg">
        <h2 className="text-xl  mb-4">
          {questionData ? 'Edit Question' : 'Add Question'}
        </h2>

        {/* Question */}
        <div className="mb-4">
          <label className="text-sm font-medium text-labelColour">
            Question
          </label>

          <input
            type="text"
            className="w-full border rounded-lg px-3 py-2 mt-1"
            placeholder="Enter question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
        </div>

        {/* Type */}
        <Dropdown
          label="Question Type"
          options={questionTypeData}
          onSelect={handleSelectQuestionType}
          preselectedValue={preSelectedQuestionType}
        />
        {/* Options */}
        {/* For type 2 & 3 */}
        {questionType !== 1 && questionType !== 8 && (
          <div className="mt-4">
            <label className="text-sm font-medium text-labelColour">
              Options
            </label>
            {options.map((opt, i) => (
              <div key={opt.id} className="flex gap-2 mt-2 items-center">
                <input
                  type="text"
                  value={opt.value}
                  onChange={(e) => handleOptionChange(e.target.value, i)}
                  className="rounded-lg h-[2.813rem] border dark:focus-visible:ring-[#4E4E4E] dark:text-white focus-visible:ring-1 focus-visible:ring-black text-black text-base font-medium dark:bg-[#101010] dark:border-[#4F4F4F] w-full py-2 px-3 focus:outline-none"
                  placeholder={`Option ${i + 1}`}
                />

                <button
                  onClick={() => removeOption(i)}
                  className="text-red-500"
                >
                  <span className="material-symbols-outlined text-xl cursor-pointer">
                    close
                  </span>
                </button>
              </div>
            ))}

            <button
              onClick={addOption}
              className="text-[#375AE7] mt-2 text-sm font-medium"
            >
              + Add Option
            </button>
          </div>
        )}

        {/* For type 4 (checkbox with % inputs) */}
        {questionType === 410 && (
          <div className="mt-4">
            {options.map((opt, i) => (
              <div key={i} className="flex items-center gap-3 mt-2">
                {/* Checkbox */}
                <input
                  type="checkbox"
                  checked={opt.checked || false}
                  onChange={() => handleCheckboxChange(i)}
                  className="w-5 h-5 text-indigo-600 border-gray-300 rounded"
                />

                {/* Label */}
                <input
                  type="text"
                  value={opt.value}
                  onChange={(e) => handleOptionChange(e.target.value, i)}
                  className="flex-1 border rounded-lg px-3 py-2"
                  placeholder={`Option ${i + 1}`}
                />

                {/* Percent Input */}
                <input
                  type="number"
                  value={opt.percent || ''}
                  onChange={(e) => handlePercentChange(i, e.target.value)}
                  className={`w-20 border rounded px-2 py-1 text-right ${
                    !opt.value ? 'bg-gray-100' : ''
                  }`}
                  placeholder="%"
                  min={0}
                  max={100}
                  disabled={!opt.value}
                />

                {/* Remove button */}
                <span
                  onClick={() => removeOption(i)}
                  className="material-symbols-outlined text-red-500 text-xl cursor-pointer"
                >
                  close
                </span>
              </div>
            ))}

            <button
              onClick={addOption}
              className="text-[#375AE7] mt-2 text-sm font-medium"
            >
              + Add Option
            </button>
          </div>
        )}

        {/* Required */}
        <div className="flex mt-2 items-center gap-2 mb-6">
          <input
            type="checkbox"
            checked={required}
            onChange={() => setRequired(!required)}
          />
          <span className="text-sm">Required</span>
        </div>

        {/* Actions */}
        <div className="flex justify-center gap-3">
          <button onClick={close} className="px-4 py-2 border rounded-lg">
            Cancel
          </button>

          <button
            onClick={(e) => {
              if (!question) {
                showToast('Please enter a valid question', 'error');
                return;
              }
              if (questionType != 1 && options.length == 0) {
                showToast('Please enter atleast one option', 'error');
                return;
              }

              setLoading(true);
              handleSubmit(e);
            }}
            className="px-4 py-2 bg-[#375AE7] text-white rounded-lg"
          >
            {questionData ? 'Update' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
